import { type ComponentProps } from "react";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import {
  fontStyleForGrapheme,
  graphemeDisplayText,
  graphemeFontWeightStyle,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";

type GraphemeFontGlyphProps = Omit<ComponentProps<typeof Text>, "children"> & {
  glyph: GraphemeGlyph;
  /** A grid's resolved script avoids two per-character settings subscriptions. */
  hanziScript?: ReturnType<typeof useActorUser>["hanziScript"];
};

function ResolvedGraphemeFontGlyph({
  className,
  glyph,
  hanziScript,
  style,
  variant = "glyph",
  ...props
}: GraphemeFontGlyphProps & { hanziScript: ReturnType<typeof useActorUser>["hanziScript"] }) {
  const fontStyle = fontStyleForGrapheme(glyph.display, hanziScript);
  // Legacy CJK Strokes variation sequences are rewritten to the private-use code point carrying
  // their variant (e.g. the left-falling dot ㇔︀) because that font has an empty cmap format-14
  // table. Real UVS mappings, including Jigmo2's 𦰩󠄂, pass through unchanged.
  const displayText = graphemeDisplayText(glyph.display, hanziScript);
  return (
    <Text
      className={className}
      style={[
        fontStyle,
        // Use the continuous weight axis for CJK as well as Latin.
        graphemeFontWeightStyle(glyph.fontWeight),
        // Only force a color when the glyph carries one (e.g. a pinyin/prevalence tone color). When it's
        // null, leave color unset so the theme-aware `text-foreground` className wins — a hardcoded
        // fallback would render black and become invisible in dark mode.
        glyph.color === null ? undefined : { color: glyph.color },
        style,
      ]}
      variant={variant}
      {...props}
    >
      {displayText}
    </Text>
  );
}

function SelfResolvedGraphemeFontGlyph(props: GraphemeFontGlyphProps) {
  const { hanziScript } = useActorUser();
  return <ResolvedGraphemeFontGlyph {...props} hanziScript={hanziScript} />;
}

export function GraphemeFontGlyph(props: GraphemeFontGlyphProps) {
  return props.hanziScript === undefined ? (
    <SelfResolvedGraphemeFontGlyph {...props} />
  ) : (
    <ResolvedGraphemeFontGlyph {...props} hanziScript={props.hanziScript} />
  );
}
